/**
 * Company workspace — Assigned Tests catalogue (11-iframe-embed/01 § Story 89).
 *
 * Hiring managers see only the Tests their Company is assigned. The two-step
 * model (System Admin assigns Test → hiring manager issues Share Token) is
 * the load-bearing gating mechanism per the issue § Notes.
 *
 * Data wiring: fetches from GET /api/companies/:id/assigned-tests via
 * `listAssignedTestsForCompany` in state/assignments/api.ts.
 * Company ID resolved in priority order:
 *   1. URL param :id (e.g. future /company/:id/assignments route)
 *   2. Active company from GET /api/admin/whoami response
 *   3. Empty state with guidance (no ID available)
 */

import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Shell } from '@/components/shell';
import { AssignedTestsList } from '../../../components/assignments/AssignedTestsList.tsx';
import { apiFetch } from '@/lib/api-client';
import {
  listAssignedTestsForCompany,
  type AssignedTestItem,
} from '../../../state/assignments/api.ts';

const EM_DASH = '—';

export function AssignmentsPage(): JSX.Element {
  const params = useParams<{ id: string }>();
  const [items, setItems] = useState<AssignedTestItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [errorKind, setErrorKind] = useState<'forbidden' | 'load_failure' | null>(null);
  const [companyId, setCompanyId] = useState<string>(params.id ?? '');

  // Resolve company ID: prefer URL param, fall back to whoami active_company.
  useEffect(() => {
    if (params.id) {
      setCompanyId(params.id);
      return;
    }
    // No URL param — resolve from session active company.
    void (async () => {
      try {
        const res = await apiFetch('/api/admin/whoami', { credentials: 'same-origin' });
        const json = (await res.json()) as {
          ok: boolean;
          data?: { active_company?: { id: string } | null };
        };
        if (json.ok && json.data?.active_company?.id) {
          setCompanyId(json.data.active_company.id);
        }
      } catch {
        // Leave companyId as empty — will show honest empty state.
      }
    })();
  }, [params.id]);

  // Fetch assigned tests once company ID is known.
  useEffect(() => {
    if (!companyId) return;
    let cancelled = false;
    setItems(null);
    setError(null);
    setErrorKind(null);
    void (async () => {
      try {
        const page = await listAssignedTestsForCompany('', companyId);
        if (!cancelled) {
          setItems(page.items);
        }
      } catch (err) {
        if (!cancelled) {
          const msg = err instanceof Error ? err.message : '';
          // Detect 403/not-a-member errors from the unwrap helper
          if (msg === 'not an active member of this Company' || msg.includes('forbidden') || msg.includes('403')) {
            setErrorKind('forbidden');
            setError('forbidden');
          } else {
            setErrorKind('load_failure');
            setError('Failed to load assigned tests. Please try again.');
          }
          setItems([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [companyId]);

  const isLoading = items === null;

  return (
    <Shell mode="company" densityMode="workspace" companyId={companyId}>
      <div className="mx-auto max-w-page px-page-pad-x py-12">
        <header className="mb-8">
          <p className="font-mono text-mono text-muted-2">
            COMPANY · ASSIGNED TESTS
          </p>
          <h1 className="mt-2 font-sans text-headline font-medium tracking-tight">
            Assigned Tests
          </h1>
          <p className="mt-2 max-w-prose font-sans text-body text-muted">
            Tests your Company can use to assess candidates. A System Admin
            grants access per Test; once granted you can issue Share Tokens —
            including embeddable iframe Tokens — against any listed Test.
          </p>
        </header>

        {error && errorKind === 'forbidden' ? (
          <div className="rounded border border-line bg-card p-8 text-center mb-4" role="alert">
            <p className="font-sans text-title font-medium text-ink">You don&apos;t have access to this company.</p>
            <p className="mt-2 font-sans text-small text-muted">
              You are not an active member of this company workspace.
              Contact your workspace administrator to request access.
            </p>
          </div>
        ) : error && errorKind === 'load_failure' ? (
          <div className="rounded border border-error/30 bg-error/5 p-4 mb-4" role="alert">
            <p className="font-sans text-small text-error">
              Something went wrong loading assigned tests. Please try again.
            </p>
          </div>
        ) : null}

        <section aria-labelledby="assigned-tests">
          <h2 id="assigned-tests" className="mb-4 font-sans text-title font-medium">
            {isLoading ? EM_DASH : `Catalogue (${String(items.length)})`}
          </h2>
          {isLoading ? (
            <p className="font-mono text-mono text-muted" aria-live="polite">
              {EM_DASH}
            </p>
          ) : (
            <AssignedTestsList items={items} />
          )}
        </section>
      </div>
    </Shell>
  );
}
